import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Match {
  id: string;
  leagueId?: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  fieldId?: string;
  status: string;
  homeScore?: number;
  awayScore?: number;
  notes?: string;
  refereeId?: string;
  homeTeam: { id: string; name: string; teamNumber?: string } | null;
  awayTeam: { id: string; name: string; teamNumber?: string } | null;
  league?: { id: string; name: string } | null;
  referee?: { id: string; firstName: string; lastName: string } | null;
}

interface MatchEvent {
  id: string;
  matchId: string;
  type: 'goal' | 'assist' | 'yellow_card' | 'red_card';
  playerId: string;
  teamId: string;
  minute?: number;
  notes?: string;
  player?: { id: string; firstName: string; lastName: string } | null;
  team?: { id: string; name: string } | null;
}

const MatchDetailGeneral: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [matchEvents, setMatchEvents] = useState<MatchEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [fieldId, setFieldId] = useState<string | null>(null);

  // RSVP state
  const [myRsvp, setMyRsvp] = useState<string | null>(null);
  const [rsvpData, setRsvpData] = useState<{
    homeTeam: { attending: number; notAttending: number; maybe: number; rsvps: Array<{ id: string; userId: string; status: string; firstName: string; lastName: string }> };
    awayTeam: { attending: number; notAttending: number; maybe: number; rsvps: Array<{ id: string; userId: string; status: string; firstName: string; lastName: string }> };
  } | null>(null);
  const [userTeamId, setUserTeamId] = useState<string | null>(null);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [showRsvpList, setShowRsvpList] = useState(false);

  // Admin reschedule state
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');

  // Admin reassign referee state
  const [assigningRef, setAssigningRef] = useState(false);
  const [referees, setReferees] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [selectedRefereeId, setSelectedRefereeId] = useState('');

  useEffect(() => {
    if (id) {
      fetchMatch();
      fetchMatchEvents();
      findFieldByLocation();
    }
  }, [id]);

  // Fetch RSVP data once match is loaded
  useEffect(() => {
    if (!match || !user || match.status !== 'scheduled') return;

    const checkMembershipAndFetchRsvps = async () => {
      try {
        // Check if user is on home or away team
        const homeTeamId = match.homeTeamId;
        const awayTeamId = match.awayTeamId;

        let foundTeamId: string | null = null;
        for (const teamId of [homeTeamId, awayTeamId]) {
          try {
            const membersRes = await api.get(`/teams/${teamId}/members`);
            const isMember = membersRes.data.some((m: any) => m.userId === user.id);
            if (isMember) {
              foundTeamId = teamId;
              break;
            }
          } catch {
            // Skip
          }
        }

        setUserTeamId(foundTeamId);

        if (foundTeamId) {
          // Fetch user's RSVP and full RSVP data
          const [mineRes, allRes] = await Promise.all([
            api.get(`/matches/${id}/rsvp/mine`),
            api.get(`/matches/${id}/rsvps`),
          ]);
          setMyRsvp(mineRes.data?.status || null);
          setRsvpData(allRes.data);
        }
      } catch (error) {
        console.error('Error fetching RSVP data:', error);
      }
    };

    checkMembershipAndFetchRsvps();
  }, [match, user, id]);

  const fetchMatch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/matches/${id}`);
      const matchData = response.data;

      // Fetch league info if available
      if (matchData.leagueId) {
        try {
          const leagueRes = await api.get(`/leagues/${matchData.leagueId}`);
          matchData.league = {
            id: leagueRes.data.id,
            name: leagueRes.data.name,
          };
        } catch (err) {
          console.error('Error fetching league:', err);
        }
      }

      // Fetch referee info if available
      if (matchData.refereeId) {
        try {
          const refereeRes = await api.get(`/users/${matchData.refereeId}`);
          matchData.referee = {
            id: refereeRes.data.id,
            firstName: refereeRes.data.firstName,
            lastName: refereeRes.data.lastName
          };
        } catch (err) {
          console.error('Error fetching referee:', err);
        }
      }

      setMatch(matchData);
    } catch (error) {
      console.error('Error fetching match:', error);
      alert('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchEvents = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/matches/${id}/events`);
      setMatchEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching match events:', error);
      // Don't show error if events endpoint doesn't exist or match has no events
    }
  };

  const findFieldByLocation = async () => {
    if (!id) return;
    
    try {
      const matchRes = await api.get(`/matches/${id}`);
      
      // Use fieldId directly from match if available
      if (matchRes.data.fieldId) {
        setFieldId(matchRes.data.fieldId);
        return;
      }
      
      // Fallback to searching by location if fieldId not present
      const location = matchRes.data.location;
      if (!location) return;

      // Search for field by name
      const fieldsRes = await api.get('/fields', { params: { page: '1', limit: '100' } });
      const fields = fieldsRes.data.fields || fieldsRes.data || [];
      
      // Try to find field by exact name match
      const field = fields.find((f: any) => 
        f.name.toLowerCase() === location.toLowerCase() ||
        f.address?.toLowerCase().includes(location.toLowerCase())
      );

      if (field) {
        setFieldId(field.id);
      }
    } catch (error) {
      console.error('Error finding field:', error);
    }
  };

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  const fetchReferees = async (leagueId?: string) => {
    try {
      const response = await api.get('/users');
      const allUsers = response.data.users || response.data || [];
      const refereeUsers = allUsers.filter((u: any) => u.profileType === 'referee');

      let filteredReferees = refereeUsers;
      if (leagueId) {
        filteredReferees = refereeUsers.filter((ref: any) => {
          if (!ref.allowedLeagueIds || ref.allowedLeagueIds.length === 0) return true;
          return ref.allowedLeagueIds.includes(leagueId);
        });
      }

      setReferees(filteredReferees.map((u: any) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName })));
    } catch (error) {
      console.error('Error fetching referees:', error);
    }
  };

  const handleReschedule = async () => {
    if (!match) return;
    try {
      const body: any = {};
      if (editDate && editDate !== match.scheduledDate) body.scheduledDate = editDate;
      if (editTime && editTime !== match.scheduledTime) body.scheduledTime = editTime;
      if (!body.scheduledDate && !body.scheduledTime) {
        setEditing(false);
        return;
      }
      await api.patch(`/matches/${id}/reschedule`, body);
      setEditing(false);
      fetchMatch();
    } catch (error: any) {
      console.error('Error rescheduling match:', error);
      alert(error.response?.data?.error || 'Failed to reschedule match');
    }
  };

  const handleAssignReferee = async () => {
    try {
      await api.patch(`/matches/${id}/assign-referee`, {
        refereeId: selectedRefereeId || null
      });
      setAssigningRef(false);
      setSelectedRefereeId('');
      fetchMatch();
    } catch (error: any) {
      console.error('Error assigning referee:', error);
      alert(error.response?.data?.error || 'Failed to assign referee');
    }
  };

  const handleRsvp = async (status: string) => {
    if (!id || rsvpSubmitting) return;
    setRsvpSubmitting(true);
    try {
      await api.post(`/matches/${id}/rsvp`, { status });
      setMyRsvp(status);
      // Refresh RSVP data
      const allRes = await api.get(`/matches/${id}/rsvps`);
      setRsvpData(allRes.data);
    } catch (error: any) {
      console.error('Error submitting RSVP:', error);
      alert(error.response?.data?.error || 'Failed to submit RSVP');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const handleLocationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLocationModal(true);
  };

  const handleGoogleMaps = () => {
    if (!match?.location) return;
    const query = encodeURIComponent(match.location);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    setShowLocationModal(false);
  };

  const handleFieldView = () => {
    if (fieldId) {
      navigate(`/fields/${fieldId}`);
    } else {
      alert('Field information not available for this location');
    }
    setShowLocationModal(false);
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!match) {
    return (
      <div className="container">
        <div className="card">
          <h1>Match not found</h1>
          <Link to="/matches" className="btn btn-secondary">
            Back to Matches
          </Link>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'scheduled':
        return <span className="badge badge-info">Scheduled</span>;
      case 'in_progress':
        return <span className="badge badge-warning">In Progress</span>;
      case 'cancelled':
        return <span className="badge badge-danger">Cancelled</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '20px' }}>
        <Link to="/matches" className="btn btn-secondary">← Back to Matches</Link>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h1>
          {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
        </h1>
        {match.league && (
          <p style={{ color: '#757575', fontSize: '16px' }}>
            {match.league.name}
          </p>
        )}
        <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <p><strong>Date:</strong> {new Date(match.scheduledDate).toLocaleDateString()}</p>
            <p><strong>Time:</strong> {match.scheduledTime || 'TBD'}</p>
          </div>
          <div>
            <p>
              <strong>Location:</strong>{' '}
              <span
                style={{ 
                  color: '#2E7D32', 
                  cursor: 'pointer', 
                  textDecoration: 'underline',
                  fontWeight: '500'
                }}
                onClick={handleLocationClick}
              >
                {match.location}
              </span>
            </p>
            <p><strong>Status:</strong> {getStatusBadge(match.status)}</p>
          </div>
          <div>
            <p>
              <strong>Referee:</strong>{' '}
              {match.referee ? (
                <Link to={`/users/${match.referee.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                  {match.referee.firstName} {match.referee.lastName}
                </Link>
              ) : (
                <span style={{ color: '#757575' }}>Not assigned</span>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 10px' }}
                  onClick={() => {
                    if (assigningRef) {
                      setAssigningRef(false);
                    } else {
                      setAssigningRef(true);
                      fetchReferees(match.leagueId);
                      setSelectedRefereeId(match.refereeId || '');
                    }
                  }}
                >
                  {assigningRef ? 'Cancel' : match.referee ? 'Change Ref' : 'Assign Ref'}
                </button>
              )}
            </p>
            {assigningRef && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={selectedRefereeId}
                  onChange={(e) => setSelectedRefereeId(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                >
                  <option value="">Unassign</option>
                  {referees.map(r => (
                    <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
                  ))}
                </select>
                <button className="btn btn-primary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={handleAssignReferee}>
                  Save
                </button>
              </div>
            )}
          </div>
        </div>

        {isAdmin && match.status === 'scheduled' && (
          <div style={{ marginTop: '15px' }}>
            {!editing ? (
              <button
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px' }}
                onClick={() => {
                  setEditing(true);
                  setEditDate(match.scheduledDate);
                  setEditTime(match.scheduledTime || '');
                }}
              >
                Reschedule
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '13px' }}>Date</label>
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '13px' }}>Time</label>
                  <input
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                  />
                </div>
                <button className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 16px' }} onClick={handleReschedule}>
                  Save
                </button>
                <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '8px 16px' }} onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {match.status === 'completed' && match.homeScore !== undefined && match.awayScore !== undefined && (
          <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
            <h2 style={{ fontSize: '2rem', margin: '0' }}>
              {match.homeScore} - {match.awayScore}
            </h2>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Team Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div>
            <h3>Home Team</h3>
            <p>
              <Link to={`/teams/${match.homeTeamId}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                {match.homeTeam?.name || 'TBD'}
              </Link>
            </p>
            {match.homeTeam?.teamNumber && (
              <p style={{ color: '#757575' }}>Team #{match.homeTeam.teamNumber}</p>
            )}
          </div>
          <div>
            <h3>Away Team</h3>
            <p>
              <Link to={`/teams/${match.awayTeamId}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                {match.awayTeam?.name || 'TBD'}
              </Link>
            </p>
            {match.awayTeam?.teamNumber && (
              <p style={{ color: '#757575' }}>Team #{match.awayTeam.teamNumber}</p>
            )}
          </div>
        </div>
      </div>

      {/* RSVP Section - visible to team members for scheduled matches */}
      {match.status === 'scheduled' && userTeamId && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>RSVP</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Let your team know if you can make it
          </p>

          {/* RSVP Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              className={myRsvp === 'attending' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => handleRsvp('attending')}
              disabled={rsvpSubmitting}
              style={{
                backgroundColor: myRsvp === 'attending' ? '#2E7D32' : undefined,
                borderColor: myRsvp === 'attending' ? '#2E7D32' : undefined,
                padding: '10px 20px',
              }}
            >
              Attending
            </button>
            <button
              className={myRsvp === 'maybe' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => handleRsvp('maybe')}
              disabled={rsvpSubmitting}
              style={{
                backgroundColor: myRsvp === 'maybe' ? '#F9A825' : undefined,
                borderColor: myRsvp === 'maybe' ? '#F9A825' : undefined,
                padding: '10px 20px',
              }}
            >
              Maybe
            </button>
            <button
              className={myRsvp === 'not_attending' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => handleRsvp('not_attending')}
              disabled={rsvpSubmitting}
              style={{
                backgroundColor: myRsvp === 'not_attending' ? '#C62828' : undefined,
                borderColor: myRsvp === 'not_attending' ? '#C62828' : undefined,
                padding: '10px 20px',
              }}
            >
              Not Attending
            </button>
          </div>

          {/* RSVP Counts */}
          {rsvpData && (() => {
            const teamData = userTeamId === match.homeTeamId ? rsvpData.homeTeam : rsvpData.awayTeam;
            return (
              <div>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#2E7D32', fontWeight: '500' }}>
                    Attending: {teamData.attending}
                  </span>
                  <span style={{ color: '#F9A825', fontWeight: '500' }}>
                    Maybe: {teamData.maybe}
                  </span>
                  <span style={{ color: '#C62828', fontWeight: '500' }}>
                    Not Attending: {teamData.notAttending}
                  </span>
                </div>

                {/* Expandable player list */}
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRsvpList(!showRsvpList)}
                  style={{ fontSize: '13px', padding: '6px 14px', marginBottom: '10px' }}
                >
                  {showRsvpList ? 'Hide Players' : 'Show Players'}
                </button>

                {showRsvpList && (
                  <div style={{ marginTop: '10px' }}>
                    {(['attending', 'maybe', 'not_attending'] as const).map((statusKey) => {
                      const label = statusKey === 'attending' ? 'Attending' : statusKey === 'maybe' ? 'Maybe' : 'Not Attending';
                      const color = statusKey === 'attending' ? '#2E7D32' : statusKey === 'maybe' ? '#F9A825' : '#C62828';
                      const players = teamData.rsvps.filter(r => r.status === statusKey);
                      if (players.length === 0) return null;
                      return (
                        <div key={statusKey} style={{ marginBottom: '12px' }}>
                          <h4 style={{ color, marginBottom: '5px' }}>{label}</h4>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {players.map(p => (
                              <li key={p.id}>{p.firstName} {p.lastName}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {match.status === 'completed' && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>Match Events</h2>
          
          {(() => {
            const goalEvents = matchEvents.filter(e => e.type === 'goal');
            const assistEvents = matchEvents.filter(e => e.type === 'assist');
            const yellowCardEvents = matchEvents.filter(e => e.type === 'yellow_card');
            const redCardEvents = matchEvents.filter(e => e.type === 'red_card');

            const getTeamName = (teamId: string) => {
              if (teamId === match.homeTeamId) return match.homeTeam?.name || 'Home Team';
              if (teamId === match.awayTeamId) return match.awayTeam?.name || 'Away Team';
              return 'Unknown Team';
            };

            if (matchEvents.length === 0) {
              return <p style={{ color: '#757575' }}>No events recorded for this match.</p>;
            }

            return (
              <>
                {goalEvents.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3>⚽ Goals</h3>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Team</th>
                          <th>Minute</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goalEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              {event.player ? (
                                <Link 
                                  to={`/players/${event.playerId}`} 
                                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                                >
                                  {event.player.firstName} {event.player.lastName}
                                </Link>
                              ) : (
                                'Unknown'
                              )}
                            </td>
                            <td>{getTeamName(event.teamId)}</td>
                            <td>{event.minute || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {assistEvents.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3>🎯 Assists</h3>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Team</th>
                          <th>Minute</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assistEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              {event.player ? (
                                <Link 
                                  to={`/players/${event.playerId}`} 
                                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                                >
                                  {event.player.firstName} {event.player.lastName}
                                </Link>
                              ) : (
                                'Unknown'
                              )}
                            </td>
                            <td>{getTeamName(event.teamId)}</td>
                            <td>{event.minute || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {yellowCardEvents.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3>🟨 Yellow Cards</h3>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Team</th>
                          <th>Minute</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yellowCardEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              {event.player ? (
                                <Link 
                                  to={`/players/${event.playerId}`} 
                                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                                >
                                  {event.player.firstName} {event.player.lastName}
                                </Link>
                              ) : (
                                'Unknown'
                              )}
                            </td>
                            <td>{getTeamName(event.teamId)}</td>
                            <td>{event.minute || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {redCardEvents.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3>🟥 Red Cards</h3>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Team</th>
                          <th>Minute</th>
                        </tr>
                      </thead>
                      <tbody>
                        {redCardEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              {event.player ? (
                                <Link 
                                  to={`/players/${event.playerId}`} 
                                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                                >
                                  {event.player.firstName} {event.player.lastName}
                                </Link>
                              ) : (
                                'Unknown'
                              )}
                            </td>
                            <td>{getTeamName(event.teamId)}</td>
                            <td>{event.minute || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {match.notes && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>Notes</h2>
          <p>{match.notes}</p>
        </div>
      )}

      {showLocationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
            <h2 style={{ marginTop: 0 }}>Location Options</h2>
            <p style={{ color: '#757575', marginBottom: '20px' }}>
              How would you like to view the location?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                className="btn btn-primary"
                onClick={handleGoogleMaps}
                style={{ width: '100%' }}
              >
                📍 Open in Google Maps
              </button>
              {fieldId ? (
                <button
                  className="btn btn-secondary"
                  onClick={handleFieldView}
                  style={{ width: '100%' }}
                >
                  🏟️ View Field Details
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  disabled
                  style={{ width: '100%', opacity: 0.5 }}
                  title="Field information not available for this location"
                >
                  🏟️ View Field Details (Not Available)
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setShowLocationModal(false)}
                style={{ width: '100%' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetailGeneral;

